import { Button, Checkbox, Col, Flex, Radio, Row, Select, Switch } from "antd";
import { FormikErrors, FormikTouched } from "formik";
import Parser from "html-react-parser";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { authApi } from "@api";
import { enumValidateKey, genderOptions } from "@configs";
import {
    IAmphur,
    IDate,
    IEmailForm,
    IHandleBlur,
    IHandleChange,
    IHandleChangeSelect,
    IProvince,
    ISelect,
    ISubDistrict,
    ISurveyQuestions,
} from "@interfaces";
import { selectMerchant } from "@redux";
import { formatMessage, splitMessage, useNotify } from "@utils";
import React, { useEffect, useState } from "react";
import { SharedButton, SharedInput, StyledError } from "..";
import DynamicDropdown from "../shared/DynamicDropdown";

interface IProps {
    values: IEmailForm;
    handleBlur: IHandleBlur;
    handleChange: IHandleChange;
    errors: FormikErrors<IEmailForm>;
    touched: FormikTouched<IEmailForm>;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
    isSubmitting: boolean;
    days: ISelect[];
    onChangeDay: IHandleChangeSelect;
    onChangeMonth: IHandleChangeSelect;
    onChangeYear: IHandleChangeSelect;
    months: ISelect[];
    years: ISelect[];
    date: IDate;
    surveyQuestions: ISurveyQuestions[];
    handleChangeAnswerByIndex: (answer: string | string[], gIndex: number, fIndex: number) => void;
    showTermCondition: () => void;
    termCondition: string;
    showTerm: boolean;
    handleAnswerText: (value: any, gIndex: number, fIndex: number) => void;
    onChangeGender: (value: string) => void;
    ansGroup: any[];
    addressData: any;
    setAddressData: (value: any) => void;
    provinceList: IProvince[];
    amphurList: IAmphur[];
    subDistrictList: ISubDistrict[];
    onChangeProvince: (value: any) => void;
    onChangeAmphur: (value: any) => void;
    onChangeSubDistrict: (value: any) => void;
    setAddressText: (value: any, name: string) => void;
    onChangeMemberType: (value: string) => void;
    isFullName?: boolean;
    isMemberApply?: boolean;
    setMember: (value: any) => void;
    member?: any;
    toggleEmail: boolean;
    setToggleEmail: (value: boolean) => void;
    isEmailPrefix?: boolean;
    checkValidateKey: () => void;
    validateKey: string;
    disableSelectType?: boolean;
}

const { Option } = Select;

export const ComponentFromTel = (props: IProps) => {
    const {
        days,
        months,
        years,
        onChangeDay,
        onChangeYear,
        onChangeMonth,
        values,
        handleBlur,
        handleChange,
        errors,
        touched,
        handleSubmit,
        isSubmitting,
        date,
        surveyQuestions,
        termCondition,
        showTerm,
        handleChangeAnswerByIndex,
        showTermCondition,
        handleAnswerText,
        onChangeGender,
        ansGroup,
        addressData,
        provinceList,
        amphurList,
        subDistrictList,
        onChangeProvince,
        onChangeAmphur,
        onChangeSubDistrict,
        setAddressText,
        onChangeMemberType,
        isFullName,
        isMemberApply,
        setMember,
        member,
        toggleEmail,
        setToggleEmail,
        isEmailPrefix,
        checkValidateKey,
        validateKey,
        disableSelectType,
    } = props;
    //page hook
    const { t } = useTranslation();
    //page variable
    const merchant = useSelector(selectMerchant);
    const { themes } = useSelector(selectMerchant);
    const staticQuestion = merchant.signUpSettings?.staticQuestion;
    const { error, success, warning } = useNotify();
    const [isSendEmail, setIsSendEmail] = useState<boolean>(false);
    const [emailCode, setEmailCode] = useState<string>("");
    const [countDown, setCountDown] = useState<number>(60);
    const [isActiveCountDown, setIsActiveCountDown] = useState<boolean>(false);

    const startCountdown = () => {
        setCountDown(60);
        setIsActiveCountDown(true);
    };
    const verifyEmail = async () => {
        if (values.email) {
            try {
                await authApi
                    .verifyEmail({ email: values.email, merchantId: merchant._id })
                    .then((res) => {
                        if (res.data.message === true) {
                            setIsSendEmail(true);
                            startCountdown();
                            setEmailCode("");
                        } else {
                            warning("Email is not correct");
                        }
                    })
                    .catch((err) => {
                        error(formatMessage(err.response));
                    });
            } catch (err: any) {
                error(formatMessage(err.response));
            }
        } else warning("Please fill email");
    };
    const verifyEmailCode = async (code: string) => {
        if (emailCode) {
            try {
                await authApi
                    .verifyEmailCode({
                        email: values.email,
                        code: code,
                        merchantId: merchant._id,
                    })
                    .then((res) => {
                        if (res.data.message === true) {
                            success("Verify success");
                            setMember(res.data.result);
                        } else {
                            warning("Verify code is not correct");
                        }
                    });
            } catch (err: any) {
                warning(formatMessage(err.response));
            }
        } else warning("Please fill email code");
    };

    useEffect(() => {
        let timerId: NodeJS.Timeout;
        if (isActiveCountDown && countDown > 0) {
            timerId = setInterval(() => {
                setCountDown((prevSeconds) => prevSeconds - 1);
            }, 1000);
        } else if (countDown === 0) {
            setIsActiveCountDown(false);
        }

        return () => clearInterval(timerId); // Clear interval on component unmount or when timer stops
    }, [isActiveCountDown, countDown]);

    return (
        <StyledContainer showTerm={showTerm} mainColor={themes?.themeColor!}>
            <form onSubmit={handleSubmit}>
                {isFullName ? (
                    <SharedInput
                        placeholder={t("page.name_lastname")}
                        name="fullName"
                        value={values.fullName}
                        onBlur={handleBlur}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            //cut double space
                            e.target.value = e.target.value.replace(/\s{2,}/g, " ");
                            e.target.value = e.target.value.replace(/\./g, "");
                            handleChange(e);
                        }}
                        errors={errors.fullName}
                        touched={touched.fullName}
                        style={{ marginBottom: "25px" }}
                    />
                ) : (
                    <div>
                        <div className="name">
                            <SharedInput
                                placeholder={t("page.name")}
                                name="fName"
                                value={values.fName}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                errors={errors.fName}
                                touched={touched.fName}
                            />
                        </div>
                        <div className="name">
                            <SharedInput
                                placeholder={t("page.surname")}
                                name="lName"
                                value={values.lName}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                errors={errors.lName}
                                touched={touched.lName}
                            />
                        </div>
                    </div>
                )}

                {staticQuestion?.email?.isActive && validateKey === enumValidateKey.EMAIL && (
                    <div style={{ marginBottom: "20px" }}>
                        <Row gutter={8}>
                            <Col span={18}>
                                <SharedInput
                                    placeholder={
                                        staticQuestion?.email?.isRequired
                                            ? "*" + t("page.email")
                                            : t("page.email")
                                    }
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col span={6}>
                                <Button className="buttonVerify" onClick={checkValidateKey}>
                                    {t("page.verify")}
                                </Button>
                            </Col>
                        </Row>
                    </div>
                )}
                {staticQuestion?.id_card?.isActive &&
                    validateKey === enumValidateKey.NATIONAL_ID && (
                        <div style={{ marginBottom: "20px" }}>
                            <Row gutter={8}>
                                <Col span={18}>
                                    <SharedInput
                                        placeholder={
                                            staticQuestion?.id_card?.isRequired
                                                ? "*" + t("page.cardId")
                                                : t("page.cardId")
                                        }
                                        name="id_card"
                                        value={values.id_card}
                                        onChange={handleChange}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Button className="buttonVerify" onClick={checkValidateKey}>
                                        {t("page.verify")}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    )}
                {staticQuestion?.staffID?.isActive && validateKey === enumValidateKey.STAFF_ID && (
                    <div style={{ marginBottom: "20px" }}>
                        <Row gutter={8}>
                            <Col span={18}>
                                <SharedInput
                                    placeholder={
                                        staticQuestion?.staffID?.isRequired
                                            ? "*" + t("page.staffID")
                                            : t("page.staffID")
                                    }
                                    name="staffID"
                                    value={values.staffID}
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col span={6}>
                                <Button className="buttonVerify" onClick={checkValidateKey}>
                                    {t("page.verify")}
                                </Button>
                            </Col>
                        </Row>
                    </div>
                )}
                {staticQuestion?.studentID?.isActive &&
                    validateKey === enumValidateKey.STUDENT_ID && (
                        <div style={{ marginBottom: "20px" }}>
                            <Row gutter={8}>
                                <Col span={18}>
                                    <SharedInput
                                        placeholder={
                                            staticQuestion?.staffID?.isRequired
                                                ? "*" + t("page.studentID")
                                                : t("page.studentID")
                                        }
                                        name="studentID"
                                        value={values.studentID}
                                        onChange={handleChange}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Button className="buttonVerify" onClick={checkValidateKey}>
                                        {t("page.verify")}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    )}

                {staticQuestion?.id_card?.isActive &&
                    validateKey !== enumValidateKey.NATIONAL_ID && (
                        <SharedInput
                            placeholder={
                                staticQuestion?.id_card?.isRequired
                                    ? "*" + t("page.cardId")
                                    : t("page.cardId")
                            }
                            name="id_card"
                            style={{ marginBottom: "25px" }}
                            onChange={handleChange}
                            value={values.id_card}
                            length={13}
                        />
                    )}
                {staticQuestion?.dateOfBirth?.isActive && (
                    <div className="day">
                        <Row gutter={16}>
                            <Col span={8}>
                                <DynamicDropdown
                                    setValue={date.day || undefined}
                                    value="id"
                                    label="label"
                                    style={{ width: "100%" }}
                                    options={days}
                                    handleChange={onChangeDay}
                                    placeholder={
                                        staticQuestion?.dateOfBirth?.isRequired
                                            ? "*" + t("page.day")
                                            : t("page.day")
                                    }
                                />
                            </Col>
                            <Col span={8}>
                                <DynamicDropdown
                                    setValue={date.month || undefined}
                                    value="id"
                                    label="label"
                                    style={{ width: "100%" }}
                                    options={months}
                                    handleChange={onChangeMonth}
                                    placeholder={
                                        staticQuestion?.dateOfBirth?.isRequired
                                            ? "*" + t("page.month")
                                            : t("page.month")
                                    }
                                />
                            </Col>
                            <Col span={8}>
                                <DynamicDropdown
                                    setValue={date.year || undefined}
                                    value="id"
                                    label="label"
                                    style={{ width: "100%" }}
                                    options={years}
                                    handleChange={onChangeYear}
                                    placeholder={
                                        staticQuestion?.dateOfBirth?.isRequired
                                            ? "*" + t("page.year")
                                            : t("page.year")
                                    }
                                />
                            </Col>
                        </Row>
                        {date.error && (
                            <StyledError visible={true}>{t("validation.is_required")} </StyledError>
                        )}
                    </div>
                )}
                {staticQuestion?.email?.isActive &&
                    !isMemberApply &&
                    validateKey !== enumValidateKey.EMAIL &&
                    !merchant.securityConfig?.shouldVerifyOtp && (
                        <div className="email">
                            {/* <div className="title">{t("page.email")}</div> */}
                            <SharedInput
                                placeholder={
                                    staticQuestion?.email?.isRequired
                                        ? "*" + t("page.email")
                                        : t("page.email")
                                }
                                name="email"
                                value={values.email}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                errors={errors.email}
                                touched={touched.email}
                                style={{ marginBottom: "25px" }}
                            />
                        </div>
                    )}
                {staticQuestion?.email?.isActive &&
                    !isMemberApply &&
                    merchant.securityConfig?.shouldVerifyOtp && (
                        <div>
                            <div className="email" style={{ marginBottom: "10px" }}>
                                {/* <div className="title">{t("page.email")}</div> */}

                                {!member ? (
                                    <Row gutter={8}>
                                        <Col span={18}>
                                            <SharedInput
                                                disable={member}
                                                placeholder={
                                                    staticQuestion?.email?.isRequired
                                                        ? "*" + t("page.email")
                                                        : t("page.email")
                                                }
                                                name="email"
                                                value={values.email}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                errors={errors.email}
                                                touched={touched.email}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            {isSendEmail &&
                                            isActiveCountDown &&
                                            !member &&
                                            countDown !== 0 ? (
                                                <Button
                                                    className="resendOtp"
                                                    disabled={!member && countDown !== 0}
                                                    onClick={() => {
                                                        if (
                                                            values.email?.endsWith(
                                                                merchant.securityConfig
                                                                    ?.emailDomainToVerify!
                                                            )
                                                        ) {
                                                            verifyEmail();
                                                        } else {
                                                            error(
                                                                splitMessage(
                                                                    "Invalid email || อีเมลไม่ถูกต้อง"
                                                                )
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {t("page.resend")} {countDown}
                                                    {" S"}
                                                </Button>
                                            ) : (
                                                <Button
                                                    className="buttonOtp"
                                                    disabled={member}
                                                    onClick={() => {
                                                        if (
                                                            values.email?.endsWith(
                                                                merchant.securityConfig
                                                                    ?.emailDomainToVerify!
                                                            )
                                                        ) {
                                                            verifyEmail();
                                                        } else {
                                                            error(
                                                                splitMessage(
                                                                    "Invalid email || อีเมลไม่ถูกต้อง"
                                                                )
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {t("page.get_otp")}
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                ) : (
                                    <div className="emailSuccess">
                                        <SharedInput
                                            disable={member}
                                            placeholder={t("page.email")}
                                            name="email"
                                            value={values.email}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            errors={errors.email}
                                            touched={touched.email}
                                            suffixIcon={<img src={"../images/7Check.svg"} />}
                                            style={{ borderRadius: "12px" }}
                                        />
                                    </div>
                                )}
                            </div>
                            {isSendEmail && !member && (
                                <div className="email-code" style={{ marginBottom: "10px" }}>
                                    <Row gutter={8}>
                                        <Col span={18}>
                                            <SharedInput
                                                disable={member}
                                                placeholder={"รหัส OTP ยืนยันอีเมล"}
                                                name="emailOtp"
                                                value={emailCode}
                                                onBlur={handleBlur}
                                                length={6}
                                                onChange={(e: any) => {
                                                    setEmailCode(e.target.value);
                                                    // if (e.target.value.length === 6) {
                                                    //     verifyEmailCode(e.target.value);
                                                    // }
                                                }}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <Button
                                                className="buttonOtp"
                                                disabled={member}
                                                onClick={() => verifyEmailCode(emailCode)}
                                            >
                                                {t("page.verify")}
                                            </Button>
                                        </Col>
                                    </Row>
                                    {/* <Input.OTP
                                    disabled={member}
                                    length={6}
                                    onChange={(val: string) => {
                                        setEmailCode(val);
                                    }}
                                /> */}
                                </div>
                            )}
                        </div>
                    )}
                {staticQuestion?.email?.isActive && isMemberApply && isEmailPrefix && (
                    <div style={{ marginBottom: "10px" }}>
                        <Switch
                            value={toggleEmail}
                            onChange={setToggleEmail}
                            style={{
                                backgroundColor: toggleEmail ? merchant.themes?.themeColor : "",
                            }}
                        />
                        <span style={{ fontSize: "14px", marginLeft: "10px" }}> Email partner</span>
                    </div>
                )}

                {staticQuestion?.email?.isActive &&
                    isMemberApply &&
                    !toggleEmail &&
                    validateKey !== enumValidateKey.EMAIL && (
                        <div className="email">
                            {/* <div className="title">{t("page.email")}</div> */}
                            <SharedInput
                                placeholder={
                                    staticQuestion?.email?.isRequired
                                        ? "*" + t("page.email")
                                        : t("page.email")
                                }
                                name="email"
                                value={values.email}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                errors={errors.email}
                                touched={touched.email}
                                style={{ marginBottom: "25px" }}
                            />
                        </div>
                    )}

                {staticQuestion?.email?.isActive &&
                    isMemberApply &&
                    toggleEmail &&
                    validateKey !== enumValidateKey.EMAIL && (
                        <div>
                            <div className="email" style={{ marginBottom: "10px" }}>
                                {/* <div className="title">{t("page.email")}</div> */}

                                {!member ? (
                                    <Row gutter={8}>
                                        <Col span={18}>
                                            <SharedInput
                                                disable={member}
                                                placeholder={
                                                    staticQuestion?.email?.isRequired
                                                        ? "*" + t("page.email")
                                                        : t("page.email")
                                                }
                                                name="email"
                                                value={values.email}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                errors={errors.email}
                                                touched={touched.email}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            {isSendEmail &&
                                            isActiveCountDown &&
                                            !member &&
                                            countDown !== 0 ? (
                                                <Button
                                                    className="resendOtp"
                                                    disabled={!member && countDown !== 0}
                                                    onClick={() => verifyEmail()}
                                                >
                                                    {t("page.resend")} {countDown}
                                                    {" S"}
                                                </Button>
                                            ) : (
                                                <Button
                                                    className="buttonOtp"
                                                    disabled={member}
                                                    onClick={verifyEmail}
                                                >
                                                    {t("page.get_otp")}
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                ) : (
                                    <div className="emailSuccess">
                                        <SharedInput
                                            disable={member}
                                            placeholder={t("page.email")}
                                            name="email"
                                            value={values.email}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            errors={errors.email}
                                            touched={touched.email}
                                            suffixIcon={<img src={"../images/7Check.svg"} />}
                                            style={{ borderRadius: "12px" }}
                                        />
                                    </div>
                                )}
                            </div>
                            {/* {isSendEmail && isActiveCountDown && !member && (
                            <div className="text-count-down">{countDown}</div>
                        )} */}
                            {/* {isSendEmail && countDown === 0 && !member && (
                            <div
                                style={{ width: "100%", display: "flex", justifyContent: "center" }}
                            >
                                <Button
                                    type="text"
                                    style={{ color: merchant.themeColor, textAlign: "center" }}
                                    onClick={verifyEmail}
                                >
                                    Resend Code
                                </Button>
                            </div>
                        )} */}
                            {isSendEmail && !member && (
                                <div className="email-code" style={{ marginBottom: "10px" }}>
                                    <Row gutter={8}>
                                        <Col span={18}>
                                            <SharedInput
                                                disable={member}
                                                placeholder={"รหัส OTP ยืนยันอีเมล"}
                                                name="emailOtp"
                                                value={emailCode}
                                                onBlur={handleBlur}
                                                length={6}
                                                onChange={(e: any) => {
                                                    setEmailCode(e.target.value);
                                                    // if (e.target.value.length === 6) {
                                                    //     verifyEmailCode(e.target.value);
                                                    // }
                                                }}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <Button
                                                className="buttonOtp"
                                                disabled={member}
                                                onClick={() => verifyEmailCode(emailCode)}
                                            >
                                                {t("page.verify")}
                                            </Button>
                                        </Col>
                                    </Row>
                                    {/* <Input.OTP
                                    disabled={member}
                                    length={6}
                                    onChange={(val: string) => {
                                        setEmailCode(val);
                                    }}
                                /> */}
                                </div>
                            )}
                        </div>
                    )}
                {staticQuestion?.gender?.isActive && (
                    <div className="gender">
                        <Radio.Group
                            onChange={(e) => onChangeGender(e.target.value)}
                            value={values.gender}
                            style={{ width: "100%", padding: "0 12px" }}
                        >
                            <Row gutter={16}>
                                <Flex
                                    align="center"
                                    justify="space-evenly"
                                    style={{ width: "100%" }}
                                >
                                    {genderOptions.map((option, index) => (
                                        <Flex key={index} flex={"1"}>
                                            <Radio key={option.id} value={option.id}>
                                                {t(`page.${option.id}`)}
                                            </Radio>
                                        </Flex>
                                    ))}
                                </Flex>
                            </Row>
                        </Radio.Group>
                    </div>
                )}
                {staticQuestion?.staffArea?.isActive &&
                    validateKey !== enumValidateKey.NATIONAL_ID && (
                        <SharedInput
                            placeholder={
                                staticQuestion?.staffArea?.isRequired
                                    ? "*" + t("page.staffArea")
                                    : t("page.staffArea")
                            }
                            name="staffArea"
                            style={{ marginBottom: "25px" }}
                            onChange={handleChange}
                            value={values.staffArea}
                            length={13}
                        />
                    )}
                {merchant?.memberTypeConfig?.isActiveOnSignUp && (
                    <div className="memberType">
                        <br />
                        <Row gutter={16}>
                            <Col span={24}>
                                <Select
                                    style={{ width: "100%" }}
                                    placeholder={t("page.member_type.pre_title")}
                                    value={values?.memberType || undefined}
                                    onChange={(val: string) => onChangeMemberType(val)}
                                    disabled={disableSelectType}
                                >
                                    {/* {merchant?.memberTypeConfig?.memberTypeGroupName[0]?.typeGroupNameList?.map(
                                        (option: any) => {
                                            return (
                                                <Option key={option._id} value={option._id}>
                                                    {option.typeName}
                                                </Option>
                                            );
                                        }
                                    )} */}
                                    {merchant?.memberTypeConfig?.memberTypeGroupName
                                        ?.flatMap(
                                            (group: { typeGroupNameList: any[] }) =>
                                                group?.typeGroupNameList
                                        )
                                        .map((option: any) => {
                                            return (
                                                <Option key={option._id} value={option._id}>
                                                    {option.typeName}
                                                </Option>
                                            );
                                        })}
                                </Select>
                            </Col>
                        </Row>
                    </div>
                )}
                {!staticQuestion?.address?.isRedeem && (
                    <StyledSurvey mainColor={themes?.themeColor!}>
                        <br />
                        {/* <div className="group-title-section">
                            <div className="color-section">&nbsp;</div>
                            <div className="group-title">Address</div>
                        </div> */}
                        <div>
                            {/* <p className="input-title ">{t("page.address")}</p> */}
                            <SharedInput
                                placeholder={
                                    staticQuestion?.address?.isRequired
                                        ? "*" + t("page.address")
                                        : t("page.address")
                                }
                                name="freeTextAnswer"
                                value={addressData?.address}
                                onChange={(e: React.ChangeEvent<HTMLFormElement>) =>
                                    setAddressText(e.target.value, "address")
                                }
                                styleParent={{ height: "48px" }}
                                style={{
                                    backgroundColor: "white",
                                    // border: "1px solid gray",
                                }}
                            />
                            <div>
                                <Row gutter={16}>
                                    {/* <Col span={12} style={{ lineHeight: "27px" }}>
                                        <p className="input-title ">{t("page.province")}</p>
                                    </Col>
                                    <Col span={12} style={{ lineHeight: "27px" }}>
                                        <p className="input-title ">{t("page.county")}</p>
                                    </Col> */}
                                    <Col span={12} style={{ paddingTop: "27px" }}>
                                        <DynamicDropdown
                                            value="province_id"
                                            setValue={addressData?.province}
                                            label="province_name_tha"
                                            style={{ width: "100%" }}
                                            options={provinceList}
                                            handleChange={onChangeProvince}
                                            placeholder={t("page.province")}
                                        />
                                    </Col>
                                    <Col span={12} style={{ paddingTop: "27px" }}>
                                        <DynamicDropdown
                                            value="amphur_id"
                                            setValue={addressData?.district}
                                            label="amphur_name_tha"
                                            style={{ width: "100%" }}
                                            options={amphurList}
                                            handleChange={onChangeAmphur}
                                            placeholder={t("page.county")}
                                        />
                                    </Col>
                                </Row>
                            </div>
                            <div>
                                <Row gutter={16}>
                                    {/* <Col span={12}>
                                        <p className="input-title ">{t("page.district")}</p>
                                    </Col>
                                    <Col span={12}>
                                        <p className="input-title ">{t("page.zipcode")}</p>
                                    </Col> */}
                                    <Col span={12} style={{ paddingTop: "27px" }}>
                                        <DynamicDropdown
                                            value="district_id"
                                            setValue={addressData?.subDistrict}
                                            label="district_name_tha"
                                            style={{ width: "100%" }}
                                            options={subDistrictList}
                                            handleChange={onChangeSubDistrict}
                                            placeholder={t("page.district")}
                                        />
                                    </Col>
                                    <Col span={12} style={{ paddingTop: "27px" }}>
                                        <SharedInput
                                            name="freeTextAnswer"
                                            value={addressData?.postCode}
                                            placeholder={t("page.zipcode")}
                                            onChange={(e: React.ChangeEvent<HTMLFormElement>) =>
                                                setAddressText(e.target.value, "postCode")
                                            }
                                            styleParent={{ height: "48px" }}
                                            style={{
                                                backgroundColor: "white",
                                                // border: "1px solid gray",
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </StyledSurvey>
                )}

                <StyledSurvey mainColor={themes?.themeColor!}>
                    {ansGroup?.map((gItem, gIndex) => {
                        if (gItem?.isActiveOnSignUp) {
                            return (
                                <div key={gIndex}>
                                    <br />
                                    <div className="group-title-section">
                                        <div className="color-section">&nbsp;</div>
                                        <div className="group-title">{gItem?.groupName}</div>
                                    </div>
                                    {gItem?.fields &&
                                        gItem?.fields?.map((fItem: any, fIndex: number) => {
                                            return (
                                                <div key={fIndex}>
                                                    <p
                                                        className={
                                                            "input-title " +
                                                            `${fItem?.multiple && "multiple-title"}`
                                                        }
                                                    >
                                                        {fItem.question}
                                                        {fItem?.isRequired && (
                                                            <span style={{ color: "red" }}>*</span>
                                                        )}
                                                    </p>
                                                    {fItem?.isFreeTextAnswer === true && (
                                                        <>
                                                            <SharedInput
                                                                placeholder={fItem.displayName}
                                                                name="freeTextAnswer"
                                                                value={fItem?.answers}
                                                                onBlur={handleBlur}
                                                                onChange={(
                                                                    e: React.ChangeEvent<HTMLFormElement>
                                                                ) =>
                                                                    handleAnswerText(
                                                                        e.target.value,
                                                                        gIndex,
                                                                        fIndex
                                                                    )
                                                                }
                                                                styleParent={{ height: "48px" }}
                                                                style={{
                                                                    backgroundColor: "white",
                                                                    border: "1px solid gray",
                                                                }}
                                                                errors={errors.freeTextAnswer}
                                                                touched={touched.freeTextAnswer}
                                                            />
                                                        </>
                                                    )}
                                                    {fItem?.isFreeTextAnswer === false && (
                                                        <Select
                                                            style={{ width: "100%" }}
                                                            showSearch
                                                            placeholder={fItem.displayName}
                                                            value={fItem?.answers}
                                                            onChange={(value: string[]) =>
                                                                handleChangeAnswerByIndex(
                                                                    value,
                                                                    gIndex,
                                                                    fIndex
                                                                )
                                                            }
                                                            mode={
                                                                fItem.multiple
                                                                    ? "multiple"
                                                                    : undefined
                                                            }
                                                            showArrow={true}
                                                            // open={true}
                                                        >
                                                            {fItem.options.map((option: any) => {
                                                                return (
                                                                    <Option
                                                                        key={option.optionName}
                                                                        value={option.optionName}
                                                                    >
                                                                        {option.optionName}
                                                                    </Option>
                                                                );
                                                            })}
                                                        </Select>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            );
                        }
                    })}
                </StyledSurvey>
                <div className="bottom">
                    <div className="term-field">
                        <StyledCheck color={themes?.themeColor!}>
                            <Checkbox
                                name="acceptTermsAndConditions"
                                checked={values.acceptTermsAndConditions}
                                onClick={handleChange}
                            />
                            <span>{t("page.accept")} </span>
                        </StyledCheck>
                        <span
                            style={{
                                textDecoration: "underline",
                                cursor: "pointer",
                                marginLeft: 4,
                                marginBottom: 2,
                            }}
                            onClick={showTermCondition}
                        >
                            {t("page._terms_and_conditions")}
                        </span>
                    </div>
                    <div
                        className="accept-text-err"
                        style={
                            errors.acceptTermsAndConditions && touched.acceptTermsAndConditions
                                ? { color: "red", visibility: "visible" }
                                : {}
                        }
                    >
                        {errors.acceptTermsAndConditions || "---"}
                    </div>
                    <StyledButton
                        size="small"
                        bg_color={themes?.themeColor!}
                        disabled={isSubmitting}
                        type="submit"
                    >
                        {t("page.sign_up")}
                    </StyledButton>
                </div>
            </form>
            <div className="term-condition">{Parser(termCondition)}</div>
        </StyledContainer>
    );
};

const StyledContainer = styled.div<{ showTerm: boolean; mainColor: string }>`
    position: relative;
    padding: 11px;
    background-color: #fff;
    color: #676767;
    text-align: start;
    font-weight: normal;
    font-size: 20px;
    line-height: 27px;
    .term-condition {
        display: ${(p) => (p.showTerm ? "unset;" : "none;")};
        width: 100%;
        height: 100%;
        background-color: ${(p) => p.theme.colors.card};
        position: absolute;
        top: 0;
        left: 0;
        font-size: 14px;
        overflow: auto;
        z-index: 100;
        ::-webkit-scrollbar {
            display: none;
        }
    }
    .day {
        margin-bottom: 27px;
        border-radius: 6px;
        .title {
            font-size: 16px;
            color: #676767;
        }
    }
    .name {
        margin-bottom: 27px;
        border-radius: 6px;
        .title {
            font-size: 16px;
            color: #676767;
        }
    }
    .gender {
        margin-bottom: 27px;
    }
    .email {
        width: 100%;
        border-radius: 6px;

        .title {
            font-size: 16px;
            color: #676767;
        }
        .ant-radio-checked {
            .ant-radio-inner {
                border-color: ${(p) => p.mainColor};
                background-color: ${(p) => p.mainColor};
            }
        }
    }
    .buttonOtp {
        display: flex;
        height: 48px;
        padding: 14px 12px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: var(--12-px, 12px);
        border: 1px solid var(--Primary-linear, ${(p) => p.mainColor});
        background-color: white;
        color: ${(p) => p.mainColor};
    }
    .buttonVerify {
        display: flex;
        height: 48px;
        width: 100%;
        padding: 14px 12px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: var(--12-px, 12px);
        border: 1px solid var(--Primary-linear, ${(p) => p.mainColor});
        background-color: white;
        color: ${(p) => p.mainColor};
    }
    .resendOtp {
        display: flex;
        width: 81px;
        height: 54px;
        padding: var(--14-px, 14px) var(--12-px, 12px);
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: var(--12-px, 12px);
        background-color: #efefef;
        color: #9b9b9b;
        font-size: 8px;
        font-style: normal;
        font-weight: 500;
        border: none;
    }
    .emailSuccess {
        width: 100%;
        border-radius: 6px;
        background-color: transparent;
    }
    .email-code {
        // display: flex;
        // flex-direction: column;
        width: 100%;
        border-radius: 6px;
        // align-items: center;
    }
    .text-count-down {
        color: ${(p) => p.mainColor};
        text-align: center;
        padding: 10px 0;
    }
    .bottom {
        width: fit-content;
        margin: 0 auto;
        margin-top: 5rem;
        text-align: center;
        span {
            font-weight: normal;
            font-size: 16px;
            line-height: 21px;
            color: #676767;
        }
        /* .ant-checkbox-wrapper {
            display: block;
        } */
    }
    .ant-select-selection {
        /* border-color: gray !important; */
        box-shadow: none !important;
        height: 48px;
        font-size: 16px;
        color: inherit;
        display: flex;
        flex-direction: column;
        justify-content: center;
        background-color: #fff;
        border-radius: 6px;
        border: 1px solid lightgray;
    }
    input {
        background-color: white !important;
        border: 1px solid lightgray;
    }

    input[type="search"] {
        background-color: transparent !important;
    }

    .intl-tel-input input {
        border-radius: 6px;
    }
    .term-field {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
    }
    .cross-line {
        background-color: #f1f1f1;
        height: 15px;
        margin-top: 40px;
    }

    .ant-select-arrow {
        color: ${(p) => p.mainColor} !important;

        stroke-width: 50px;
        stroke: ${(p) => p.mainColor};
    }

    .ant-checkbox-inner {
        border-color: ${(p) => p.mainColor};
    }
    :hover .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner {
        background-color: ${(p) => p.mainColor} !important;
    }
    :not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-inner,
    :where(.css-dev-only-do-not-override-1kuana8).ant-checkbox:not(.ant-checkbox-disabled):hover
        .ant-checkbox-inner {
        border-color: ${(p) => p.mainColor};
    }
`;

const StyledButton = styled(SharedButton)`
    margin-top: 24px;
`;

const StyledCheck = styled.div<{ color: string }>`
    display: flex;
    gap: 5px;
`;

const StyledSurvey = styled.div<{ mainColor: string }>`
    /* padding: 20px ; */
    p {
        color: #3c3c3c;
        font-size: 16px;
        margin-bottom: 0px;
    }
    .group-title-section {
        display: flex;
        .color-section {
            background-color: ${(p) => p.mainColor};
            border-radius: 5px;
        }
        .group-title {
            font-size: 18px;
            font-weight: 600;
            /* border-left: 4px solid ${(p) => p.mainColor}; */
            padding-left: 10px;
            line-height: 30px;
            /* margin-top: 3px; */
        }
    }
    .ant-select-selection {
        box-shadow: none !important;
        height: 48px;
        font-size: 16px;
        color: inherit;
        display: flex;
        flex-direction: column;
        justify-content: center;
        border-radius: 6px;
        background-color: #fff;
        border: 1px solid #dadada !important;
        cursor: default;
        width: 100%;
    }

    .intl-tel-input input {
        border-radius: 6px;
    }
    .term-field {
        display: flex;
        justify-content: center;
    }
    .btn-section {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 20px;
        .btn {
            width: 100%;
            height: 55px;
            border-radius: 500px;
            font-size: 16px;
        }
        .back {
            color: #f22f46;
            background-color: #fff;
            border: 1px solid #f22f46;
        }
        .save {
            //
            background-color: #f22f46;
            color: white;
        }
    }
    .input-title {
        margin: 12px 0 8px 0;
        color: #919191;
        font-size: 16px;
        font-weight: 500;
    }
    .multiple-title {
        margin-bottom: 10px;
        line-height: 16px;
    }
    .ant-select-selection--multiple {
        .ant-select-arrow {
            margin-top: 0px;
        }
    }
`;
